import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Modal,
  Button,
  ListGroup,
  Form,
  Spinner
} from 'react-bootstrap';
import Select from 'react-select';
import { supabase } from '../../supabaseClient';
import '../../assets/css/checklist.css';
import ConfirmationPopup from './ConfirmationPopup';
import EditPopup from './EditPopup';
import AddTaskPopUp from './AddTaskPopup';
import PortalDropdown from './PortalDropdown';
import TaskAssistantChat from './TaskAssistantChat';
import ChatAssistantInput from './ChatAssistantInput';
import MultiSelectFilterBar from './MultiSelectFilterBar'
import QuickSharePopup from './QuickSharePopup';
import ShareSectionPopup from './ShareSectionPopup';
import { TbFilterCog } from 'react-icons/tb'

const createDefaultStatusFilters = () => new Set();
const getNormalizedStatus = (task) => (task?.status === 'completed' ? 'completed' : 'pending');

const GroceryExportMorePopup = ({
  show,
  handleClose,
  title,
  fetchItems,
  isDarkMode,
  UserId,
  initialSearchTerm = '',
  initialCategory = '',
  initialSubcategory = ''
}) => {
  const [tasks, setTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState({});
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [selectedSubcategory, setSelectedSubcategory] = useState(initialSubcategory);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [taskToRemove, setTaskToRemove] = useState(null);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sharedGroceries, setSharedGroceries] = useState([])
  const [sharedCategories, setSharedCategories] = useState([])
  const [sentGroceryIds, setSentGroceryIds] = useState(new Set())
  const [sentCategoryIds, setSentCategoryIds] = useState(new Set())

  // Multi-select delete states
  const [checkedSessions, setCheckedSessions] = useState([]);
  const [deleteAllChecked, setDeleteAllChecked] = useState(false);
  const [showDeleteDropdown, setShowDeleteDropdown] = useState(false);
  const [deleteFilter, setDeleteFilter] = useState('all');
  const [statusFilters, setStatusFilters] = useState(createDefaultStatusFilters);
  const [showBulkDeleteConfirmation, setShowBulkDeleteConfirmation] = useState(false);
  const [showModalFilters, setShowModalFilters] = useState(false);
  const [quickShareState, setQuickShareState] = useState(null)
  const [showSharePopup, setShowSharePopup] = useState(false)

  // Chat assistant states
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatWidthPercent, setChatWidthPercent] = useState(70);
  const [isResizing, setIsResizing] = useState(false);
  const chatAssistantRef = useRef(null);
  const modalBodyRef = useRef(null);
  const searchTimeoutRef = useRef(null); // For debounced search

  // Resize handler for chat width
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !modalBodyRef.current) return;

    const modalBody = modalBodyRef.current;
    const modalRect = modalBody.getBoundingClientRect();
    const mouseX = e.clientX - modalRect.left;
    const modalWidth = modalRect.width;

    const chatWidth = modalWidth - mouseX;
    let newPercent = (chatWidth / modalWidth) * 100;

    newPercent = Math.max(20, Math.min(70, newPercent));
    setChatWidthPercent(newPercent);
  }, [isResizing]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Fetch shared groceries received by this user
  const fetchSharedGroceries = useCallback(async () => {
    if (!UserId) return
    try {
      const { data: shared } = await supabase
        .from('grocery_table')
        .select('id, title, description, status, grocery_table_id, shared_by, share_visible')
        .eq('user_id', UserId)
        .eq('is_share', true)
        .not('shared_by', 'is', null)
        .order('created_at', { ascending: false })

      const items = (shared || []).map(t => ({
        ...t, _shared: true, _sharedFrom: t.shared_by || 'Unknown', _isNew: t.share_visible === false
      }))
      setSharedGroceries(items)

      const catIds = [...new Set(items.map(t => t.grocery_table_id).filter(id => id && id !== 0))]
      if (catIds.length > 0) {
        const { data: cats } = await supabase
          .from('grocery_categories')
          .select('id, name, parent_category_id')
          .in('id', catIds)
        const parentIds = (cats || []).map(c => c.parent_category_id).filter(id => id && !catIds.includes(id))
        let parentCats = []
        if (parentIds.length > 0) {
          const { data } = await supabase.from('grocery_categories').select('id, name').in('id', [...new Set(parentIds)])
          parentCats = data || []
        }
        const allCatMap = {}
        ;[...(cats || []), ...parentCats].forEach(c => { allCatMap[c.id] = c })
        const parents = []
        const seen = new Set()
        ;(cats || []).forEach(cat => {
          const pid = cat.parent_category_id || cat.id
          if (!seen.has(String(pid))) {
            seen.add(String(pid))
            const name = cat.parent_category_id ? (allCatMap[cat.parent_category_id]?.name || cat.name) : cat.name
            parents.push({ id: `shared_${pid}`, originalId: String(pid), name: `${name} (shared)` })
          }
        })
        setSharedCategories(parents)
      } else {
        setSharedCategories([])
      }
    } catch (e) { console.error('Error fetching shared groceries:', e) }
  }, [UserId])

  const fetchSentGroceryIds = useCallback(async () => {
    if (!UserId) return
    try {
      const API_URL = process.env.REACT_APP_ZOOMEY_API_URL || 'http://localhost:3000'
      const res = await fetch(`${API_URL}/api/sharing/sent-task-ids?user_id=${UserId}&section_type=grocery`)
      const data = await res.json()
      setSentGroceryIds(new Set((data?.sent_task_ids || []).map(String)))
      setSentCategoryIds(new Set((data?.sent_category_ids || []).map(String)))
    } catch (e) { console.error('Error fetching sent grocery ids:', e) }
  }, [UserId])

  const syncSharedGrocery = async (groceryId, updates) => {
    try {
      const API_URL = process.env.REACT_APP_ZOOMEY_API_URL || 'http://localhost:3000'
      const response = await fetch(`${API_URL}/api/sharing/sync-task`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_id: groceryId, user_id: UserId, updates, section_type: 'grocery' }),
      })
      if (!response.ok) {
        const data = await response.json().catch(() => null)
        console.error('Error syncing shared grocery:', data?.error || response.statusText)
      }
    } catch (e) { console.error('Error syncing shared grocery:', e) }
  }

  // Fetch categories from Supabase
  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('grocery_categories')
        .select('id, name, is_share')
        .eq('user_id', UserId)
        .is('parent_category_id', null)
        .or('is_share.is.null,is_share.eq.false');
      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch subcategories based on selected category
  const fetchSubcategories = async (categoryId) => {
    if (!categoryId) {
      setSubcategories([]);
      return [];
    }
    try {
      const { data, error } = await supabase
        .from('grocery_categories')
        .select('id, name, is_share')
        .eq('user_id', UserId)
        .eq('parent_category_id', categoryId)
        .or('is_share.is.null,is_share.eq.false');
      if (error) throw error;
      setSubcategories(data || []);
      return data || [];
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      return [];
    }
  };

  // Fetch tasks from Supabase
  const fetchTasks = useCallback(async (search = '', categoryId = '', subcategoryId = '') => {
    setLoading(true);
    try {
      let query = supabase
        .from('grocery_table')
        .select('id, title, description, status, grocery_table_id', { count: 'exact' })
        .eq('user_id', UserId)
        .or('is_share.is.null,is_share.eq.false')
        .order('created_at', { ascending: false })
        .ilike('title', `%${search}%`);

      if (subcategoryId) {
        query = query.eq('grocery_table_id', subcategoryId);
      } else if (categoryId === '0' || categoryId === 0) {
        query = query.is('grocery_table_id', null);
      } else if (categoryId) {
        const { data: subcategoriesData } = await supabase
          .from('grocery_categories')
          .select('id')
          .eq('parent_category_id', categoryId);
        const subcategoryIds = subcategoriesData?.map(sub => sub.id) || [];
        query = query.in('grocery_table_id', [categoryId, ...subcategoryIds]);
      }

      const { data, error } = await query;
      if (error) throw error;

      setTasks(data || []);
      const completed = {};
      (data || []).forEach(task => {
        completed[task.id] = getNormalizedStatus(task) === 'completed';
      });
      setCompletedTasks(completed);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh shared groceries and sent grocery ids when userId is available
  useEffect(() => {
    if (UserId) { fetchSharedGroceries(); fetchSentGroceryIds(); }
  }, [UserId, fetchSharedGroceries, fetchSentGroceryIds])

  // Update state when popup opens with initial values
  useEffect(() => {
    if (show) {
      setSearchTerm(initialSearchTerm);
      setSelectedCategory(initialCategory);
      setSelectedSubcategory(initialSubcategory);
      setStatusFilters(createDefaultStatusFilters());
      fetchCategories();
      // Fetch subcategories if initial category is set
      if (initialCategory) {
        fetchSubcategories(initialCategory);
      }
    }
  }, [show, initialSearchTerm, initialCategory, initialSubcategory]);

  // Fetch tasks when filters change
  useEffect(() => {
    if (show) {
      fetchTasks(searchTerm, selectedCategory, selectedSubcategory);
    }
  }, [show, searchTerm, selectedCategory, selectedSubcategory]);

  // Reset delete selections when filters change
  useEffect(() => {
    setDeleteAllChecked(false);
    setCheckedSessions([]);
  }, [show, selectedCategory, selectedSubcategory, searchTerm]);

  // Handle category change
  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setSelectedCategory(categoryId);
    setSelectedSubcategory('');
    fetchSubcategories(categoryId);
    fetchTasks(searchTerm, categoryId);
  };

  // Handle subcategory change
  const handleSubcategoryChange = (e) => {
    const subcategoryId = e.target.value;
    setSelectedSubcategory(subcategoryId);
    fetchTasks(searchTerm, selectedCategory, subcategoryId);
  };

  // Handle search term change with debounce
  const handleSearchChange = (e) => {
    const search = e.target.value;
    setSearchTerm(search);
    
    // Debounce the search to prevent race conditions
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      fetchTasks(search, selectedCategory, selectedSubcategory);
    }, 300);
  };
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Toggle task completion
  const toggleTaskCompletion = async (taskId, isShared = false) => {
    const sharedTask = isShared ? sharedGroceries.find(t => t.id === taskId) : null;
    const isCurrentlyCompleted = isShared ? sharedTask?.status === 'completed' : completedTasks[taskId];
    const previousStatus = isShared ? (sharedTask?.status || 'pending') : null;
    const newStatus = isCurrentlyCompleted ? 'pending' : 'completed';
    if (isShared) {
      setSharedGroceries(prev => prev.map(task =>
        task.id === taskId ? { ...task, status: newStatus } : task
      ));
    }
    try {
      const { error } = await supabase
        .from('grocery_table')
        .update({ status: newStatus })
        .eq('id', taskId)
        .eq('user_id', UserId);
      if (error) throw error;
      if (!isShared) {
        setCompletedTasks((prev) => ({
          ...prev,
          [taskId]: !prev[taskId],
        }));
      }
      await syncSharedGrocery(taskId, { status: newStatus });
    } catch (error) {
      if (isShared) {
        setSharedGroceries(prev => prev.map(task =>
          task.id === taskId ? { ...task, status: previousStatus } : task
        ));
      }
      console.error('Error updating task status:', error);
    }
  };

  const confirmRemoveTask = (taskId) => {
    setTaskToRemove(taskId);
    setShowConfirmation(true);
  };

  const removeTask = async () => {
    try {
      const { error } = await supabase
        .from('grocery_table')
        .delete()
        .eq('id', taskToRemove);
      if (error) throw error;
      setTasks((prev) => prev.filter(task => task.id !== taskToRemove));
      setShowConfirmation(false);
      setTaskToRemove(null);
      if (fetchItems) fetchItems();
    } catch (error) {
      console.error('Error removing task:', error);
    }
  };

  const handleEditTask = (task) => {
    setTaskToEdit(task);
    setShowEditPopup(true);
  };

  const saveTask = async (updatedTask) => {
    try {
      let categoryId = updatedTask.category_id;
      let subcategoryId = updatedTask.subcategory_id;
      
      // Handle new category creation
      if (updatedTask.isNewCategory && updatedTask.newCategoryName) {
        const { data: newCat, error: catError } = await supabase
          .from('grocery_categories')
          .insert([{ name: updatedTask.newCategoryName, user_id: UserId, parent_category_id: null }])
          .select()
          .single();
        if (catError) throw catError;
        categoryId = newCat.id;
        fetchCategories(); // Refresh categories list
      }
      
      // Handle new subcategory creation
      if (updatedTask.isNewSubcategory && updatedTask.newSubcategoryName && categoryId) {
        const { data: newSubcat, error: subcatError } = await supabase
          .from('grocery_categories')
          .insert([{ name: updatedTask.newSubcategoryName, user_id: UserId, parent_category_id: categoryId }])
          .select()
          .single();
        if (subcatError) throw subcatError;
        subcategoryId = newSubcat.id;
      }
      
      const updateData = {
        title: updatedTask.title,
        description: updatedTask.description
      };
      
      // Don't change category for shared/sent items
      if (!updatedTask._shared && !updatedTask._sent) {
        // Add category if provided
        if (categoryId !== undefined) {
          updateData.grocery_table_id = subcategoryId || categoryId || null;
        }
      }
      
      const { error } = await supabase
        .from('grocery_table')
        .update(updateData)
        .eq('id', updatedTask.id);
      if (error) throw error;
      syncSharedGrocery(updatedTask.id, {
        title: updateData.title,
        description: updateData.description,
      });
      setTasks((prev) => prev.map(task => (task.id === updatedTask.id ? { ...task, ...updateData } : task)));
      setShowEditPopup(false);
      setTaskToEdit(null);
      if (fetchItems) fetchItems();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleTaskCreated = () => {
    fetchTasks(searchTerm, selectedCategory, selectedSubcategory);
    if (fetchItems) fetchItems();
  };

  // Multi-select handlers
  const handleCheckboxChange = (taskId) => {
    setCheckedSessions(prev =>
      prev.includes(taskId)
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleDeleteDropdownAll = () => {
    if (deleteAllChecked) {
      setCheckedSessions([]);
      setDeleteAllChecked(false);
    } else {
      const filteredTaskIds = deleteFilteredTasks.map(task => task.id);
      setCheckedSessions(filteredTaskIds);
      setDeleteAllChecked(true);
    }
  };

  const confirmBulkDelete = () => {
    if (checkedSessions.length === 0) return;
    setShowBulkDeleteConfirmation(true);
  };

  const handleDeleteCheckedItems = async () => {
    if (checkedSessions.length === 0) return;
    try {
      const { error } = await supabase
        .from('grocery_table')
        .delete()
        .in('id', checkedSessions);
      if (error) throw error;
      setTasks(prev => prev.filter(task => !checkedSessions.includes(task.id)));
      setCheckedSessions([]);
      setDeleteAllChecked(false);
      setShowDeleteDropdown(false);
      setShowBulkDeleteConfirmation(false);
      if (fetchItems) fetchItems();
    } catch (error) {
      console.error('Error deleting items:', error);
    }
  };

  // Status counts for filter badges
  const statusCounts = {
    pending: tasks.filter(t => getNormalizedStatus(t) === 'pending').length,
    completed: tasks.filter(t => getNormalizedStatus(t) === 'completed').length,
  };

  // Merge own + shared groceries (filter shared by selected category)
  const filteredShared = sharedGroceries.filter(st => {
    if (tasks.some(t => t.id === st.id)) return false
    if (!selectedCategory) return true
    if (selectedCategory === '0') return !st.grocery_table_id
    if (String(selectedCategory).startsWith('shared_')) {
      const origId = selectedCategory.replace('shared_', '')
      return String(st.grocery_table_id) === origId
    }
    return false
  })
  const mergedTasks = [
    ...tasks.map(t => ({ ...t, _sent: sentGroceryIds.has(String(t.id)) })),
    ...filteredShared
  ]

  // Filter tasks
  const filteredTasks = mergedTasks.filter(task => {
    const matchesSearch = task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilters.size === 0 || statusFilters.has(getNormalizedStatus(task));
    return matchesSearch && matchesStatus;
  });

  const deleteFilteredTasks = filteredTasks.filter(task => {
    if (deleteFilter === 'all') return true;
    if (deleteFilter === 'pending') return getNormalizedStatus(task) === 'pending';
    if (deleteFilter === 'completed') return getNormalizedStatus(task) === 'completed';
    return true;
  });

  // Update delete all checkbox when filter changes
  useEffect(() => {
    if (showDeleteDropdown) {
      setDeleteAllChecked(true);
      const filteredTaskIds = deleteFilteredTasks.map(task => task.id);
      setCheckedSessions(filteredTaskIds);
    }
  }, [deleteFilter, showDeleteDropdown]);

  const formatMessage = text => {
    if (!text) return '';
    let cleanedText = text
      .replace(/^"/, '')
      .replace(/"$/, '')
      .replace(/\\n/g, '');
    return cleanedText
      .replace(/###\s*(.*?)(?:\n|$)/g, '<h5>$1</h5>')
      .replace(/##\s*(.*?)(?:\n|$)/g, '<h4>$1</h4>')
      .replace(/#\s*(.*?)(?:\n|$)/g, '<h3>$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/(\d+)\.\s+(.*?)(?:\n|$)/g, '<ol start="$1"><li>$2</li></ol>')
      .replace(/`(.*?)`/g, '<code style="background-color: #f0f0f0; padding: 2px 4px; border-radius: 4px; font-family: monospace;">$1</code>')
      .replace(/\[(.*?)\]\((https?:\/\/[^\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
      .replace(/(^|[^"'>])(https?:\/\/[^\s<]+)/g, '$1<a href="$2" target="_blank" rel="noopener noreferrer">$2</a>')
      .replace(/\{(.*?)\}/g, '$1');
  };

  const renderTask = (taskId, title, description, flag, grocery_table_id, isShared = false, sharedFrom = '', isNew = false, isSent = false) => {
    const sharedTask = isShared ? sharedGroceries.find(t => t.id === taskId) : null;
    const isCompleted = isShared ? sharedTask?.status === 'completed' : completedTasks[taskId];

    return (
      <div className="d-flex align-items-start mb-3 sidelist" key={`${isShared ? 'shared_' : ''}${taskId}`}>
        {flag ? (
          <Form.Check
            type="checkbox"
            style={{
              marginTop: '10px',
              marginRight: '15px',
              cursor: 'pointer',
            }}
            checked={checkedSessions.includes(taskId)}
            onChange={() => handleCheckboxChange(taskId)}
          />
        ) : (
          <i
            className={`bi ${isCompleted ? 'bi-check2-square' : 'bi bi-square'}`}
            style={{
              fontSize: '1.2em',
              marginTop: '10px',
              marginRight: '15px',
              cursor: 'pointer',
            }}
            onClick={() => toggleTaskCompletion(taskId, isShared)}
          ></i>
        )}

        <div
          style={{ flex: 1, cursor: 'pointer' }}
          onClick={() => handleEditTask({ id: taskId, title, description, category_id: grocery_table_id, _shared: isShared, _sent: isSent })}
        >
          <div className="d-flex align-items-center gap-1">
            <p
              style={{
                fontSize: '16px',
                margin: '0',
                fontWeight: 'bolder',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                textDecoration: isCompleted ? 'line-through' : 'none',
              }}
              title={title}
            >
              {title}
            </p>
            {isNew && (
              <span style={{
                fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase',
                background: 'var(--teal-bg)', color: '#fff',
                padding: '1px 5px', borderRadius: '3px', flexShrink: 0, lineHeight: '13px',
              }}>New</span>
            )}
          </div>
          {description && (
            <p
              style={{
                fontSize: '14px',
                overflow: 'hidden',
                marginBottom: '0',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                textDecoration: isCompleted ? 'line-through' : 'none',
                color: '#666'
              }}
              title={description.replace(/<[^>]*>/g, '')}
              dangerouslySetInnerHTML={{ __html: formatMessage(description) }}
            />
          )}
          {isCompleted ? (
            <span className='message-success-label fw-normal' style={{ padding: "2px 10px" }}>Completed</span>
          ) : (
            <span className='message-success-label fw-normal' style={{ backgroundColor: 'var(--orange-bg)', padding: "2px 10px" }}>Pending</span>
          )}
          {isShared && (
            <div
              title={`Received from ${sharedFrom} — Click to copy email`}
              onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(sharedFrom); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '3px',
                fontSize: '0.65rem', color: 'var(--teal-bg)', marginTop: '4px',
                cursor: 'pointer', padding: '1px 6px', borderRadius: '4px',
                background: 'rgba(var(--teal-bg-rag), 0.08)',
              }}
            >
              <i className="bi bi-box-arrow-in-down-right" style={{ fontSize: '0.7rem' }}></i>
            </div>
          )}
          {isSent && !isShared && (
            <div
              title="Shared to others"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '3px',
                fontSize: '0.65rem', color: '#f59e0b', marginTop: '4px',
                padding: '1px 6px', borderRadius: '4px',
                background: 'rgba(245, 158, 11, 0.08)',
              }}
            >
              <i className="bi bi-box-arrow-up-right" style={{ fontSize: '0.7rem' }}></i>
            </div>
          )}
        </div>

        {!isShared && (
          <i
            className="bi bi-send"
            style={{ fontSize: '0.85rem', cursor: 'pointer', color: '#14b8a6', padding: '2px 4px' }}
            title="Quick Share"
            onClick={(e) => { e.stopPropagation(); setQuickShareState(quickShareState?.id === taskId ? null : { id: taskId, rect: e.currentTarget.getBoundingClientRect() }) }}
          ></i>
        )}
        {quickShareState?.id === taskId && (
          <QuickSharePopup
            show={true}
            onClose={() => setQuickShareState(null)}
            itemId={taskId}
            sectionType="grocery"
            UserId={UserId}
            isDarkMode={isDarkMode}
            onShareSuccess={fetchSentGroceryIds}
            anchorRect={quickShareState?.rect}
          />
        )}
        {!flag && (
          <PortalDropdown
            menuItems={[
              { label: 'Edit', icon: 'bi bi-pencil', onClick: () => handleEditTask({ id: taskId, title, description, category_id: grocery_table_id, _shared: isShared, _sent: isSent }) },
              { label: 'Remove', icon: 'bi bi-trash', onClick: () => confirmRemoveTask(taskId), danger: true, disabled: isShared || isSent, title: isShared ? 'No authority' : isSent ? 'Unshare to use' : '' }
            ]}
          >
            <i className="bi bi-three-dots-vertical" style={{ fontSize: "1em", cursor: "pointer", padding: '10px' }}></i>
          </PortalDropdown>
        )}
      </div>
    );
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" className='task-popup export-popup' centered>
      <Modal.Header closeButton>
        <Modal.Title>{title || 'Grocery List'}</Modal.Title>
        <button
          style={{ marginLeft: 'auto', marginRight: '8px', background: '#14b8a6', color: '#fff', border: 'none', borderRadius: '6px', padding: '4px 14px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
          title="Share"
          onClick={(e) => { e.stopPropagation(); setShowSharePopup(true) }}
        ><i className="bi bi-share" style={{ fontSize: '0.8rem' }}></i> Share</button>
      </Modal.Header>
      <ShareSectionPopup
        isDarkMode={isDarkMode}
        show={showSharePopup}
        handleClose={() => setShowSharePopup(false)}
        UserId={UserId}
        sectionType="grocery"
      />
      <Modal.Body ref={modalBodyRef} className='export-more' style={{ overflow: 'visible', display: 'flex', flexDirection: 'row', height: '70vh', padding: 0 }}>
        {/* Resize Handle */}
        {!isChatMinimized && (
          <div
            onMouseDown={handleResizeStart}
            style={{
              width: '14px',
              height: '100%',
              background: 'var(--border-color, #d5e5e3)',
              cursor: 'col-resize',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              order: 1,
              position: 'relative',
              zIndex: 10,
              transition: 'background 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--teal-bg, #b8d4d0)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'var(--border-color, #d5e5e3)'}
          >
            <i className="bi bi-grip-vertical" style={{ fontSize: '1rem', color: 'var(--text, #666)', opacity: 0.6 }}></i>
          </div>
        )}

        {/* Task Assistant Chat - RIGHT SIDE */}
        <div style={{
          width: isChatMinimized ? '0px' : `${chatWidthPercent}%`,
          minWidth: isChatMinimized ? '0px' : '280px',
          maxWidth: isChatMinimized ? '0px' : '70%',
          height: '100%',
          transition: isResizing ? 'none' : 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          borderLeft: isChatMinimized ? 'none' : 'none',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          position: 'relative',
          background: isChatMinimized ? 'transparent' : '#FBF9FA',
          overflow: 'visible',
          order: 2
        }}>
          <TaskAssistantChat
            ref={chatAssistantRef}
            isDarkMode={isDarkMode}
            onToggle={(minimized) => setIsChatMinimized(minimized)}
            showInput={false}
            externalInput={chatInput}
            onExternalInputChange={setChatInput}
            contextType="groceries"
            contextId={selectedCategory || null}
            categoryPath={(() => {
              const path = [];
              if (selectedCategory) {
                const cat = categories.find(c => String(c.id) === String(selectedCategory));
                if (cat) path.push(cat.name);
              }
              if (selectedSubcategory) {
                const subcat = subcategories.find(s => String(s.id) === String(selectedSubcategory));
                if (subcat) path.push(subcat.name);
              }
              return path;
            })()}
            userId={UserId}
            onItemsUpdated={() => {
              fetchTasks(searchTerm, selectedCategory, selectedSubcategory);
            }}
          />
        </div>

        {/* Grocery List Section - LEFT SIDE */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: '16px',
          order: 0
        }}>
          <div className="checklist-tabs-header" style={{ padding: 0, marginBottom: '12px' }}>
            <span style={{ fontWeight: '600', fontSize: '14px' }}>Groceries</span>
            <div className='d-flex align-items-center gap-2'>
              <button
                className={`filter-toggle-btn ${showModalFilters ? 'active' : ''}`}
                onClick={() => setShowModalFilters(!showModalFilters)}
                title={showModalFilters ? 'Hide filters' : 'Show filters'}
              >
                <TbFilterCog size={20} />
              </button>
              {!showDeleteDropdown && (
                <>
                  <Button
                    style={{
                      backgroundColor: "var(--teal-bg)",
                      color: "var(--textwhite)",
                      borderRadius: '5px',
                      marginRight: '10px',
                    }}
                    onClick={() => setShowAddPopup(true)}
                  >
                    <i className='bi bi-plus'></i>
                  </Button>
                  <Button
                    variant="outline-danger"
                    onClick={() => {
                      setSearchTerm('');
                      setDeleteFilter('all');
                      setShowDeleteDropdown(true);
                      setDeleteAllChecked(true);
                      const allTaskIds = tasks.map(task => task.id);
                      setCheckedSessions(allTaskIds);
                    }}
                  >
                    <i className='bi bi-trash'></i>
                  </Button>
                </>
              )}
            </div>
          </div>
          {showModalFilters && (
            <>
              <div className='d-flex mb-3'>
                <Select
                  className='custom-select-container me-2 flex-fill'
                  classNamePrefix='custom-select'
                  options={[
                    { value: '', label: 'All Categories' },
                    { value: '0', label: 'General' },
                    ...categories.map(category => ({
                      value: category.id,
                      label: category.name,
                      isReceived: category.is_share === true,
                      isSent: sentCategoryIds.has(String(category.id)),
                    })),
                    ...sharedCategories.map(sc => ({
                      value: sc.id,
                      label: sc.name.replace(' (shared)', ''),
                      isShared: true,
                    }))
                  ]}
                  formatOptionLabel={(option) => (
                    <div className="d-flex align-items-center gap-1">
                      {option.isReceived && <i className="bi bi-box-arrow-in-down-right" style={{ fontSize: '0.7rem', color: 'var(--teal-bg)' }}></i>}
                      {option.isSent && <i className="bi bi-box-arrow-up-right" style={{ fontSize: '0.7rem', color: '#f59e0b' }}></i>}
                      {option.isShared && <i className="bi bi-box-arrow-in-down-right" style={{ fontSize: '0.7rem', color: 'var(--teal-bg)' }}></i>}
                      <span>{option.label}</span>
                    </div>
                  )}
                  value={
                    selectedCategory === ''
                      ? { value: '', label: 'All Categories' }
                      : selectedCategory === '0' || selectedCategory === 0
                        ? { value: '0', label: 'General' }
                        : categories.find(c => String(c.id) === String(selectedCategory))
                          ? { value: selectedCategory, label: categories.find(c => String(c.id) === String(selectedCategory))?.name }
                          : sharedCategories.find(sc => sc.id === selectedCategory)
                            ? { value: selectedCategory, label: sharedCategories.find(sc => sc.id === selectedCategory)?.name.replace(' (shared)', ''), isShared: true }
                            : { value: '', label: 'All Categories' }
                  }
                  onChange={(option) => handleCategoryChange({ target: { value: option?.value || '' } })}
                  isSearchable={false}
                  placeholder="All Categories"
                />
                <Select
                  className='custom-select-container flex-fill'
                  classNamePrefix='custom-select'
                  options={[
                    { value: '', label: 'All Subcategories' },
                    ...subcategories.map(subcategory => ({
                      value: subcategory.id,
                      label: subcategory.name
                    }))
                  ]}
                  value={
                    selectedSubcategory === ''
                      ? { value: '', label: 'All Subcategories' }
                      : subcategories.find(s => String(s.id) === String(selectedSubcategory))
                        ? { value: selectedSubcategory, label: subcategories.find(s => String(s.id) === String(selectedSubcategory))?.name }
                        : { value: '', label: 'All Subcategories' }
                  }
                  isDisabled={!selectedCategory}
                  onChange={(option) => handleSubcategoryChange({ target: { value: option?.value || '' } })}
                  isSearchable={false}
                  placeholder="All Subcategories"
                />
              </div>

              {/* Status Filter Bar */}
              {!showDeleteDropdown && (
                <div style={{ marginBottom: "8px" }}>
                  <MultiSelectFilterBar
                    isDarkMode={isDarkMode}
                    filterGroups={[
                      {
                        key: "status",
                        label: "Status",
                        options: [
                          { value: "pending", label: "Pending", variant: "success", count: statusCounts.pending },
                          { value: "completed", label: "Completed", variant: "secondary", count: statusCounts.completed },
                        ],
                      },
                    ]}
                    selectedFilters={{ status: statusFilters }}
                    onFilterChange={(groupKey, newSet) => setStatusFilters(newSet)}
                    showDividers={false}
                  />
                </div>
              )}

              {!showDeleteDropdown && (
                <div className='d-flex align-items-center mb-3'>
                  <Form.Control
                    type='text'
                    placeholder='Search...'
                    className='me-3'
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                </div>
              )}
            </>
          )}
          {showDeleteDropdown && (
            <div className='d-flex align-items-center mb-3'>
              <input
                className='form-check-input'
                type='checkbox'
                style={{
                  marginRight: '10px',
                  border: '1px solid black',
                  width: '18px',
                  height: '18px'
                }}
                checked={deleteAllChecked}
                onChange={() => handleDeleteDropdownAll()}
              />
              <Select
                className='custom-select-container me-3 flex-fill'
                classNamePrefix='custom-select'
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'completed', label: 'Completed' }
                ]}
                value={
                  deleteFilter === 'all'
                    ? { value: 'all', label: 'All' }
                    : deleteFilter === 'pending'
                      ? { value: 'pending', label: 'Pending' }
                      : { value: 'completed', label: 'Completed' }
                }
                onChange={(option) => setDeleteFilter(option?.value || 'all')}
                isSearchable={false}
                placeholder="All"
              />
              <Button
                variant="danger"
                disabled={checkedSessions.length === 0}
                onClick={confirmBulkDelete}
                className='me-2'
              >
                Delete ({checkedSessions.length})
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDeleteDropdown(false);
                  setCheckedSessions([]);
                  setDeleteAllChecked(false);
                }}
              >
                Cancel
              </Button>
            </div>
          )}

          {loading ? (
            <div className='text-center'>
              <Spinner animation='border' />
            </div>
          ) : (
            <div
              className="modal-scroll p-3"
              style={{
                flexGrow: 1,
                overflowY: 'scroll',
                overflowX: 'hidden',
              }}
            >
              <ListGroup>
                {(() => {
                  const itemsToShow = showDeleteDropdown ? deleteFilteredTasks : filteredTasks;
                  return itemsToShow.length > 0 ? (
                    itemsToShow.map(task =>
                      renderTask(
                        task.id,
                        task.title,
                        task.description,
                        showDeleteDropdown,
                        task.grocery_table_id,
                        task._shared || false,
                        task._sharedFrom || '',
                        task._isNew || false,
                        task._sent || false
                      )
                    )
                  ) : (
                    <div className='text-center'>No data found</div>
                  );
                })()}
              </ListGroup>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px' }}>
        <ChatAssistantInput
          chatAssistantRef={chatAssistantRef}
          inputValue={chatInput}
          onInputChange={setChatInput}
          onClearChat={() => chatAssistantRef.current?.clearChat()}
          autoFocus={true}
        />
      </Modal.Footer>

      <ConfirmationPopup
        show={showConfirmation}
        handleClose={() => setShowConfirmation(false)}
        title="Confirm Removal"
        body="Are you sure you want to remove this grocery item? This action cannot be undone."
        handleConfirm={removeTask}
      />
      <ConfirmationPopup
        show={showBulkDeleteConfirmation}
        handleClose={() => setShowBulkDeleteConfirmation(false)}
        title="Confirm Bulk Delete"
        body={`Are you sure you want to delete ${checkedSessions.length} grocery item${checkedSessions.length === 1 ? '' : 's'}? This action cannot be undone.`}
        handleConfirm={handleDeleteCheckedItems}
      />
      <EditPopup
        show={showEditPopup}
        handleClose={() => {
          setShowEditPopup(false);
          setTaskToEdit(null);
        }}
        handleSave={saveTask}
        item={taskToEdit}
        titleAction='Grocery'
        categoriesData={categories}
        fetchSubcategories={fetchSubcategories}
        subcategoriesData={subcategories}
        handleDelete={confirmRemoveTask}
        sharedCategories={sharedCategories}
      />
      {UserId && (
        <AddTaskPopUp
          actionTitle={'Add Grocery Item'}
          show={showAddPopup}
          handleClose={() => setShowAddPopup(false)}
          handleSave={saveTask}
          categoriesData={categories}
          fetchSubcategories={fetchSubcategories}
          subcategoriesData={subcategories}
          UserId={UserId}
          onTaskCreated={handleTaskCreated}
          tableName="grocery_table"
        />
      )}
    </Modal>
  );
};

export default GroceryExportMorePopup;
